import Phaser from 'phaser';
import { COLORS, FONT_MONO } from '../style.js';

/**
 * Per-scene section nav. Modern mobile-app band designed to match
 * the bottom AppNav's visual language — dark panel surface, thin
 * cyan accent line at the top edge, an animated indicator bar
 * above the active slot. The band hosts text-label tabs (vs the
 * AppNav's icon-only tabs); two halves of one chrome system.
 *
 * Used by:
 *   - InboxScene  → ACTIVE GAMES / PAST GAMES (internal tab switch)
 *   - SettingsScene / HowToScene / AboutScene → HOW TO PLAY /
 *     SETTINGS / INFO (cross-scene navigation via scene.start())
 *
 * The component owns a Phaser.Container at (opts.x, opts.y). Add
 * it to the scene's scrollable content via container.add(tabs.root)
 * or leave it free-floating in scene space, depending on whether
 * you want the band to scroll with content or stick to the top.
 */

export interface SectionTab {
  readonly id: string;
  readonly label: string;
  /** Optional count badge rendered as " (N)" after the label. */
  readonly count?: number;
}

interface TabSlot {
  readonly id: string;
  readonly text: Phaser.GameObjects.Text;
  readonly slotCenterX: number;
  readonly hit: Phaser.GameObjects.Zone;
}

interface SectionTabsOpts {
  /** Top-left x of the band in scene/container coords. */
  readonly x: number;
  /** Top-left y of the band in scene/container coords. */
  readonly y: number;
  /** Width of the band. */
  readonly width: number;
  /** Optional band height. Defaults to 64 (matches AppNav.navHeight). */
  readonly height?: number;
  readonly items: readonly SectionTab[];
  readonly activeIndex: number;
  readonly onSelect?: (id: string) => void;
}

const DEFAULT_HEIGHT = 64;
const INDICATOR_HEIGHT = 3;
const INDICATOR_WIDTH = 88;
const ACCENT_ALPHA = 0.85;
const INDICATOR_SLIDE_MS = 220;
const TEXT_FONT_SIZE = '24px';
const TEXT_LETTER_SPACING = 2;

export class SectionTabs {
  /** Phaser container holding every visual piece of the band. Add
   *  this to a parent container (e.g. a scrollable content container)
   *  or leave it at scene-root for sticky-on-top behavior. */
  readonly root: Phaser.GameObjects.Container;

  /** Rendered height of the band — callers can advance their
   *  layout cursor by this without hardcoding the default. */
  readonly height: number;

  private readonly slots: TabSlot[] = [];
  private readonly indicator: Phaser.GameObjects.Rectangle;
  private activeIndex: number;
  private readonly onSelect: ((id: string) => void) | undefined;
  private readonly scene: Phaser.Scene;

  constructor(scene: Phaser.Scene, opts: SectionTabsOpts) {
    this.scene = scene;
    this.activeIndex = opts.activeIndex;
    this.onSelect = opts.onSelect;
    this.height = opts.height ?? DEFAULT_HEIGHT;
    const w = opts.width;
    const h = this.height;

    this.root = scene.add.container(opts.x, opts.y);

    // Solid dark panel surface — matches AppNav's bgPanelInt fill.
    const bg = scene.add.rectangle(0, 0, w, h, COLORS.bgPanelInt);
    bg.setOrigin(0, 0);
    this.root.add(bg);

    // Top accent line — 1px cyan stroke that signals "this is the
    // start of a chrome region." Mirrors the AppNav's top accent so
    // the two bands read as paired bookends.
    const accent = scene.add.graphics();
    accent.lineStyle(1, COLORS.accentCyanInt, ACCENT_ALPHA);
    accent.lineBetween(0, 0, w, 0);
    this.root.add(accent);

    // Build slots — evenly spaced across the band width.
    const slotW = w / opts.items.length;
    opts.items.forEach((item, idx) => {
      const slotCenterX = slotW * (idx + 0.5);
      const labelText =
        item.count !== undefined
          ? `${item.label} (${item.count})`
          : item.label;

      const isActive = idx === opts.activeIndex;
      const text = scene.add
        .text(slotCenterX, h / 2 + 4, labelText, {
          fontFamily: FONT_MONO,
          fontSize: TEXT_FONT_SIZE,
          color: isActive ? COLORS.accentCyanHex : COLORS.textMuted,
          fontStyle: '700',
        })
        .setLetterSpacing(TEXT_LETTER_SPACING)
        .setOrigin(0.5);
      this.root.add(text);

      // Slot hit zone — covers the entire slot height so tap targets
      // are 56px+ (AppNav's standard).
      const hit = scene.add
        .zone(slotCenterX, h / 2, slotW, h)
        .setOrigin(0.5);
      if (opts.onSelect) {
        hit.setInteractive({ useHandCursor: true });
        hit.on('pointerup', () => this.handleTap(idx));
      }
      this.root.add(hit);

      this.slots.push({ id: item.id, text, slotCenterX, hit });
    });

    // Indicator bar — sits at the very top of the band, just under
    // the cyan accent line, centered above the active slot. Animates
    // via tween on tab change.
    const activeSlot = this.slots[opts.activeIndex];
    const indicatorX = activeSlot ? activeSlot.slotCenterX : slotW / 2;
    this.indicator = scene.add.rectangle(
      indicatorX,
      INDICATOR_HEIGHT / 2 + 1,
      INDICATOR_WIDTH,
      INDICATOR_HEIGHT,
      COLORS.accentCyanInt
    );
    this.indicator.setOrigin(0.5);
    this.root.add(this.indicator);
  }

  private handleTap(idx: number): void {
    if (idx === this.activeIndex) return;
    this.setActiveIndex(idx);
    const id = this.slots[idx]?.id;
    if (id && this.onSelect) this.onSelect(id);
  }

  /** Programmatically switch the active tab. Colors update instantly;
   *  the indicator slides to the new slot with a brief tween for the
   *  "modern app" feel. */
  setActiveIndex(idx: number): void {
    if (idx === this.activeIndex) return;
    if (idx < 0 || idx >= this.slots.length) return;
    const prev = this.slots[this.activeIndex];
    if (prev) prev.text.setColor(COLORS.textMuted);
    const next = this.slots[idx];
    if (next) {
      next.text.setColor(COLORS.accentCyanHex);
      this.scene.tweens.add({
        targets: this.indicator,
        x: next.slotCenterX,
        duration: INDICATOR_SLIDE_MS,
        ease: 'Cubic.easeOut',
      });
    }
    this.activeIndex = idx;
  }
}
